import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtINR, fmtDate, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { STAGES, QUARTERS, stageChipClass } from "@/lib/constants";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Plus, PencilSimple } from "@phosphor-icons/react";
import { Field } from "./Employees";
import { MultiSelect } from "@/components/MultiSelect";

const INTERACTION_TYPES = [
  { value: "call", label: "Call" },
  { value: "meeting", label: "Meeting" },
  { value: "site_visit", label: "Site Visit" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "note", label: "Note" },
  { value: "follow_up", label: "Follow Up" },
];

export default function OpportunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [opp, setOpp] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [products, setProducts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [note, setNote] = useState("");
  const [intForm, setIntForm] = useState({ type: "call", title: "", notes: "", interaction_date: "", follow_up_date: "" });
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const openEdit = () => { setEditForm({ ...opp }); setEditOpen(true); };
  const saveEdit = async () => {
    try {
      await api.put(`/opportunities/${id}`, {
        name: editForm.name, description: editForm.description, value: Number(editForm.value) || 0,
        account_id: editForm.account_id, stage: editForm.stage, quarter: editForm.quarter,
        closure_date: editForm.closure_date || null, owner_id: editForm.owner_id,
        product_ids: editForm.product_ids || [], project_team: editForm.project_team || [],
        shared_with: editForm.shared_with || [],
      });
      toast.success("Changes saved"); setEditOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const load = async () => {
    const r = await api.get(`/opportunities/${id}`); setOpp(r.data);
    const t = await api.get(`/opportunities/${id}/timeline`); setTimeline(t.data);
    const i = await api.get(`/opportunities/${id}/interactions`); setInteractions(i.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    api.get("/accounts").then((r) => setAccounts(r.data));
    api.get("/products", { params: { status: "active" } }).then((r) => setProducts(r.data));
    api.get("/employees/lookup/all").then((r) => setEmployees(r.data));
  }, []);

  if (!opp) return <div className="p-6 text-slate-500">Loading…</div>;

  const save = async (patch) => {
    try { await api.put(`/opportunities/${id}`, patch); toast.success("Updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/opportunities/${id}/note`, { note }); setNote(""); load(); toast.success("Note added"); }
    catch (e) { toast.error("Failed"); }
  };

  const addInteraction = async () => {
    if (!intForm.title) return toast.error("Title required");
    try { await api.post(`/opportunities/${id}/interactions`, intForm); setIntForm({ type: "call", title: "", notes: "", interaction_date: "", follow_up_date: "" }); load(); toast.success("Interaction logged"); }
    catch (e) { toast.error("Failed"); }
  };

  const canEdit = user?.role === "super_admin" || opp.owner_id === user?.id || opp.created_by === user?.id;

  return (
    <div data-testid="opportunity-detail-page">
      <button className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-3" onClick={() => navigate(-1)}>
        <ArrowLeft size={14} /> Back
      </button>
      <div className="flex flex-col lg:flex-row gap-3 lg:items-end justify-between mb-6">
        <div>
          <div className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${stageChipClass(opp.stage)}`}>{opp.stage}</div>
          <h1 className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>{opp.name}</h1>
          <div className="text-sm text-slate-500">{accounts.find((a) => a.id === opp.account_id)?.company_name || "—"} • {opp.quarter} • {fmtDate(opp.closure_date)}</div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-[0.15em] text-slate-500">Value</div>
          <div className="text-3xl font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>{fmtINR(opp.value)}</div>
          <div className="mt-2 text-xs flex items-center justify-end gap-2">
            <span className="opacity-60">Created:</span>
            <span className="font-medium">{opp.create_date ? fmtDate(opp.create_date) : fmtDate(opp.created_at)}</span>
            {user?.role === "super_admin" && (
              <button
                className="text-[#fd7e14] hover:underline"
                data-testid="opp-edit-create-date"
                onClick={async () => {
                  const v = window.prompt("Set opportunity create date (YYYY-MM-DD):", opp.create_date || "");
                  if (!v) return;
                  try { await api.post(`/opportunities/${opp.id}/set-create-date`, { create_date: v }); toast.success("Updated"); load(); }
                  catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                }}
              >Change</button>
            )}
          </div>
          {canEdit && (
            <Button size="sm" variant="outline" className="mt-2" onClick={openEdit} data-testid="opp-edit-btn"><PencilSimple size={14} className="mr-1" /> Edit Opportunity</Button>
          )}
        </div>
      </div>

      {/* EDIT DIALOG */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Opportunity</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Field label="Name *"><Input value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></Field></div>
            <Field label="Account *">
              <Select value={editForm.account_id || ""} onValueChange={(v) => setEditForm({ ...editForm, account_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
                <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.company_name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Value (₹)"><Input type="number" value={editForm.value ?? 0} onChange={(e) => setEditForm({ ...editForm, value: e.target.value })} /></Field>
            <Field label="Stage">
              <Select value={editForm.stage} onValueChange={(v) => setEditForm({ ...editForm, stage: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Quarter">
              <Select value={editForm.quarter} onValueChange={(v) => setEditForm({ ...editForm, quarter: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{QUARTERS.map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Closure Date"><Input type="date" value={editForm.closure_date || ""} onChange={(e) => setEditForm({ ...editForm, closure_date: e.target.value })} /></Field>
            <Field label="Owner">
              <Select value={editForm.owner_id || ""} onValueChange={(v) => setEditForm({ ...editForm, owner_id: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Products">
                <MultiSelect options={products.map((p) => ({ value: p.id, label: p.name }))} value={editForm.product_ids || []} onChange={(v) => setEditForm({ ...editForm, product_ids: v })} placeholder="Select products" />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Project Team">
                <MultiSelect options={employees.map((e) => ({ value: e.id, label: e.full_name }))} value={editForm.project_team || []} onChange={(v) => setEditForm({ ...editForm, project_team: v })} placeholder="Assign team" />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Share With">
                <MultiSelect options={employees.map((e) => ({ value: e.id, label: e.full_name }))} value={editForm.shared_with || []} onChange={(v) => setEditForm({ ...editForm, shared_with: v })} placeholder="Grant access" />
              </Field>
            </div>
            <div className="sm:col-span-2"><Field label="Description"><Textarea value={editForm.description || ""} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} rows={4} /></Field></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button className="btn-brand" onClick={saveEdit} data-testid="opp-save-changes"><PencilSimple size={14} className="mr-1" /> Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Tabs defaultValue="overview">
            <TabsList>
              <TabsTrigger value="overview" data-testid="oppd-tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="interactions" data-testid="oppd-tab-interactions">Interactions</TabsTrigger>
              <TabsTrigger value="timeline" data-testid="oppd-tab-timeline">Timeline</TabsTrigger>
            </TabsList>
            <TabsContent value="overview" className="mt-4 space-y-4">
              <Card className="p-5 space-y-3">
                <div className="text-xs uppercase tracking-wider text-slate-500">Description</div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap">{opp.description || "No description."}</div>
              </Card>
              <Card className="p-5">
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">Add Note</div>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Quick note…" />
                <div className="mt-2 text-right">
                  <Button size="sm" onClick={addNote} className="btn-brand">Add Note</Button>
                </div>
              </Card>
            </TabsContent>
            <TabsContent value="interactions" className="mt-4 space-y-4">
              <Card className="p-5">
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">Log Interaction</div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Type">
                    <Select value={intForm.type} onValueChange={(v) => setIntForm({ ...intForm, type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{INTERACTION_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Date"><Input type="date" value={intForm.interaction_date || ""} onChange={(e) => setIntForm({ ...intForm, interaction_date: e.target.value })} /></Field>
                  <div className="sm:col-span-2"><Field label="Title *"><Input value={intForm.title} onChange={(e) => setIntForm({ ...intForm, title: e.target.value })} /></Field></div>
                  <div className="sm:col-span-2"><Field label="Notes"><Textarea value={intForm.notes} onChange={(e) => setIntForm({ ...intForm, notes: e.target.value })} /></Field></div>
                  <Field label="Follow-up Date"><Input type="date" value={intForm.follow_up_date || ""} onChange={(e) => setIntForm({ ...intForm, follow_up_date: e.target.value })} /></Field>
                </div>
                <div className="mt-3 text-right">
                  <Button onClick={addInteraction} className="btn-brand"><Plus size={14} className="mr-1" />Log</Button>
                </div>
              </Card>
              <div className="space-y-3">
                {interactions.length === 0 && <div className="text-sm text-slate-500 text-center py-6">No interactions yet.</div>}
                {interactions.map((it) => (
                  <Card key={it.id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-xs uppercase tracking-wider text-slate-500">{it.type.replace(/_/g, " ")}</div>
                        <div className="font-medium text-slate-900">{it.title}</div>
                        {it.notes && <div className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{it.notes}</div>}
                      </div>
                      <div className="text-xs text-slate-400 text-right">{fmtDate(it.interaction_date)}</div>
                    </div>
                  </Card>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="timeline" className="mt-4">
              <div className="relative pl-5 border-l border-slate-200 space-y-4">
                {timeline.length === 0 && <div className="text-sm text-slate-500">No history yet.</div>}
                {timeline.map((t) => (
                  <div key={t.id} className="relative">
                    <span className="absolute -left-[26px] top-1 w-3 h-3 rounded-full bg-[#fd7e14] ring-4 ring-white" />
                    <div className="text-xs text-slate-400">{fmtDateTime(t.created_at)}</div>
                    <div className="text-sm text-slate-700">{t.note}</div>
                    {t.from_stage && <div className="text-xs mt-1"><span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${stageChipClass(t.from_stage)}`}>{t.from_stage}</span> → <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${stageChipClass(t.to_stage)}`}>{t.to_stage}</span></div>}
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Right side: editable details */}
        <div className="space-y-4">
          <Card className="p-5 space-y-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Stage</div>
              <Select value={opp.stage} onValueChange={(v) => save({ stage: v })} disabled={!canEdit}>
                <SelectTrigger data-testid="opp-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Quarter">
                <Select value={opp.quarter} onValueChange={(v) => save({ quarter: v })} disabled={!canEdit}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{QUARTERS.map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Closure">
                <Input type="date" defaultValue={opp.closure_date || ""} onBlur={(e) => e.target.value !== (opp.closure_date || "") && save({ closure_date: e.target.value })} disabled={!canEdit} />
              </Field>
            </div>
            <Field label="Value (₹)">
              <Input type="number" defaultValue={opp.value} onBlur={(e) => Number(e.target.value) !== opp.value && save({ value: Number(e.target.value) })} disabled={!canEdit} />
            </Field>
            <Field label="Owner">
              <Select value={opp.owner_id} onValueChange={(v) => save({ owner_id: v })} disabled={!canEdit}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Products">
              <MultiSelect options={products.map((p) => ({ value: p.id, label: p.name }))} value={opp.product_ids || []} onChange={(v) => save({ product_ids: v })} placeholder="Select products" />
            </Field>
            <Field label="Project Team">
              <MultiSelect options={employees.map((e) => ({ value: e.id, label: e.full_name }))} value={opp.project_team || []} onChange={(v) => save({ project_team: v })} placeholder="Assign team" />
            </Field>
            <Field label="Shared With">
              <MultiSelect options={employees.map((e) => ({ value: e.id, label: e.full_name }))} value={opp.shared_with || []} onChange={(v) => save({ shared_with: v })} placeholder="Grant access" />
            </Field>
          </Card>
        </div>
      </div>
    </div>
  );
}
